import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { StateModel } from 'redux/reducers';
import { useSelector } from 'react-redux';
import { FormValuesModel } from '.';
import { useFormikContext } from 'formik';
import { AssociationEntries } from 'enums/onboarding/crm';
import { IDocumentAssociationInfo } from 'typings/documents/documents';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import {
  getPlaceholderForInputField,
  generateFieldPlaceholderForAdditionalField,
} from '../utils';

// components
import SubmitButtons from '../SubmitButtons';
import AdditionalField from 'components/Forms/FormComponents/AdditionalField';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import {
  Text,
  Checkbox,
  Col,
  Divider,
  FieldValidationRule,
  FormField,
  Row,
  FormInput,
  FormInputProps,
} from '@ui';

const InnerForm = () => {
  const { t } = useTranslation('form');
  const { values } = useFormikContext<FormValuesModel>();
  const currentContactId = useSelector<StateModel>(
    (state) => state.auth.contactData?._id,
  );
  const documentAssociation = useMemo<
    IDocumentAssociationInfo | undefined
  >(() => {
    return {
      id: currentContactId as string,
      type: AssociationEntries.Contact,
    };
  }, [currentContactId]);

  return (
    <>
      <Row wrap={true} justify="start">
        {values.additionalQuestions.map((e, i) => (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES} key={i}>
            <Text>{`${i + 1}. ${e.name}`}</Text>
            {e.description ? <StyledText>{e.description}</StyledText> : null}
            {!e.options.onboardingRequired && (
              <FormField
                name={`additionalQuestions.${i}.isNotApplicableOrAvailable`}
                component={Checkbox}
                shouldShowErrorMessage={false}
                additionalProps={{
                  children: t('not_applicable_or_available', { ns: 'common' }),
                }}
              />
            )}
            {e.isNotApplicableOrAvailable ? (
              <FormField<FormInputProps>
                name={`additionalQuestions.${i}.notAvailableValue`}
                component={FormInput}
                additionalProps={{
                  renderPlaceholder: (validationRules) =>
                    getPlaceholderForInputField(validationRules, true, t),
                }}
              />
            ) : (
              <FormField
                name={`additionalQuestions.${i}.value`}
                component={AdditionalField}
                additionalProps={{
                  renderPlaceholder: (validationRules: FieldValidationRule[]) =>
                    generateFieldPlaceholderForAdditionalField(
                      validationRules || [],
                      e,
                      t,
                    ),
                  field: {
                    type: e.type,
                    options: e.options.selectOptions,
                    formDocument: e.options.formDocument,
                    documentAssociation,
                  },
                }}
              />
            )}
          </Col>
        ))}
      </Row>
      <HideIfDisabledForm>
        <Divider />
        <SubmitButtons />
      </HideIfDisabledForm>
    </>
  );
};

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.colorAdditionalL1};
`;

export default InnerForm;
